import {
  Autocomplete,
  Button, Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider, TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { AutocompleteElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { isOwner } from "@/lib/auth-predicates";
import CustomSelectOption from "@/components/custom-select-option";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";


const JoinLeaveClubModal = ({
  title,
  onCancel,
  confirmLoading = false,
  onSave,
  isOpen,
}: {
  title?: string;
  confirmLoading?: boolean;
  onCancel: () => void;
  onSave: (clubIds: string[]) => void;
  isOpen: boolean;
}) => {
  const { t } = useTranslation();

  const schema = z.object({
    clubIds: z
      .array(z.string())
      .refine((arr) => arr.every((str) => /^-?\d+$/.test(str)), "Inputs must be digits")
  });

  type ClubIds = z.infer<typeof schema>;


  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm<ClubIds>({
    mode: "all",
    resolver: zodResolver(schema),
  });

  const handleSaveClick = (data: ClubIds) => {
    if (!data.clubIds || data.clubIds.length === 0) {
      enqueueSnackbar(t("fieldRequired"), { variant: "error" });
      return;
    }
    onSave(data.clubIds);
  };

  useEffect(() => {
    reset();
  }, [reset, isOpen]);

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {title && (
          <Typography variant="h6" style={{ marginTop: 15 }}>
            {title}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {title && <Divider style={{ marginTop: 10, marginBottom: 30 }} />}
        <form onSubmit={handleSubmit(handleSaveClick)} autoComplete="off" key="joinClubForm">
          <Controller  //AutocompleteElement currently doesn't support
            name="clubIds"
            control={control}
            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={value}
                onChange={(_event, newValue) => {
                  onChange(newValue);
                  setValue("clubIds", newValue); // Update the value in react-hook-form
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={t("clubId")}
                    placeholder={t("enterHere")}
                    inputRef={ref}
                    onBlur={onBlur} // Ensure onBlur is called to update the form state
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={index} />
                  ))
                }
              />
            )}
          />


          <DialogActions>
            <Button onClick={onCancel}>{t("cancel")}</Button>
            <Button type="submit" disabled={confirmLoading}>
              {t("submit")}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JoinLeaveClubModal;